
import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, CheckCircle as ActivateIcon, Search as SearchIcon, Info as Info } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData, postData } from '../../../apiService'; // Ensure postData is defined
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import BillPrint from './Bill';
import PrintIcon from '@mui/icons-material/Print';
import ReactDOM from 'react-dom';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import { Tooltip } from '@mui/material';
import { useLocation } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toWords } from 'number-to-words';
import Logo from './../Logo/QR.png';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

function SaleIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const history = useHistory();
    const { isAuthenticated, companyId, permissions, token, logout, userId } = useAuth();

    const hasCreateMasterPermission = permissions.includes('Create Transaction');
    const hasEditMasterPermission = permissions.includes('Edit Transaction');
    const hasDeleteMasterPermission = permissions.includes('Delete Transaction');

    const [isLoading, setIsLoading] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [fileData, setFileData] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const branchId = 0;
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const location = useLocation();
    const [loading, setLoading] = useState(false);

    useEffect(() => {

        // Check if reload is needed based on the flag set in localStorage
        const shouldReload = localStorage.getItem('shouldReloadCreatePage') === 'true';

        if (shouldReload) {
            localStorage.removeItem('shouldReloadCreatePage'); // Remove the flag after use
            window.location.reload(); // Reload the page once
        }

        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        //setFromDate(getFormattedStartDate());
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
    }, [isAuthenticated, companyId, branchId, token, logout]);


    useEffect(() => {
        const shouldReload = localStorage.getItem('shouldReload') === 'true';

        // Only reload data if navigated from PurchaseOrderCreate and flag is set
        if (location.state?.from === '/SaleCreate' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        else if (location.state?.from === '/SaleEdit' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }

        else if (location.state?.from === '/SaleDetails' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        else {
            // Populate data without reloading for other cases
            //setFromDate(getFormattedStartDate());
            setFromDate(getFormattedCurrentDate());
            setToDate(getFormattedCurrentDate());
            // handleClickOpen(false);
        }
    }, [location]);

    const fetchtableData = async () => {

        setData([]);
        try {
            const response = await fetchData('SaleApi', `/SaleDetails/${companyId}/${companyId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Customer Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        }
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const searchableFields = ['BillPrefix', 'FinYear', 'BillNumber', 'BillDate', 'CustomerName', 'TotalQuantity', 'TaxableAmount', 'CGSTAmount', 'SGSTAmount', 'IGSTAmount', 'GrandTotal', 'TotalQuantity'];

    const filteredData = data.filter((item) =>
        searchableFields.some((key) =>
            item[key]?.toString().toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const handleDelete = async () => {
        deleteData('SaleApi', `${deleteItemId}`, token)
            .then((response) => {
                fetchtableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('SaleApi', `${itemId}`, token)
            .then(() => {
                fetchtableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handleA4Print = async (SALEID) => {
        try {
            const response = await fetchData('SaleApi', `/SalePrint/${SALEID}`, token);
            const labelData = response.response_data;
            const labelsToPrint = Array.isArray(labelData) ? labelData : [labelData];

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
            iframeDocument.write('<html><head><style>@media print { header, footer { display: none !important; } }</style></head><body>');

            ReactDOM.render(
                <BillPrint labelData={labelData} onClose={() => document.body.removeChild(iframe)} />,
                iframeDocument.body
            );

            iframeDocument.write('</body></html>');
            iframeDocument.close();

            setTimeout(() => {
                iframe.contentWindow.print();
            }, 1000);

        } catch (error) {
            console.error('Error printing Invoice:', error);
        }
    };


    const handlePrint = async (SALEID) => {
        try {
            const response = await fetchData('SaleApi', `/SalePrint/${SALEID}`, token);
            const labelData = response.response_data;

            if (!labelData?.SalePrint || !labelData?.SalePrintItems) {
                throw new Error("Invalid invoice data.");
            }

            const header = labelData.SalePrint;
            const items = labelData.SalePrintItems;

            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [80, 300] // POS roll size
            });

            const margin = { top: 5, left: 0.8, right: 0.8 };
            const pageWidth = doc.internal.pageSize.getWidth();
            const usableWidth = pageWidth - margin.left - margin.right;

            let y = margin.top;

            // ------------------ OUTER BOX START ------------------
            const yStartOuter = margin.top - 1; // Start slightly above header

            // ------------------ HEADER ------------------
            y += 2;
            doc.setFontSize(6.5);
            doc.setFont('helvetica', 'normal');
            doc.text(`GSTIN : ${header.CompanyGSTNumber || ''}`, margin.left + 1, y);
            doc.text(`Ph : ${header.CompanyContactNumber1 || ''}`, pageWidth - margin.right - 1, y, { align: 'right' });
            y += 4;

            // Company Name
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(header.CompanyName || '', pageWidth / 2, y, { align: 'center' });
            y += 5;

            // Address
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6.5);
            const addr = doc.splitTextToSize(header.CompanyAddressLine || '', usableWidth);
            addr.forEach(line => {
                doc.text(line, pageWidth / 2, y, { align: 'center' });
                y += 3;
            });

            // Bill of Supply separator
            doc.setLineWidth(0.1);
            doc.line(margin.left, y, pageWidth - margin.right, y);
            y += 3;
            doc.setFont('helvetica', 'bold');
            doc.text("Bill of Supply", pageWidth / 2, y, { align: 'center' });
            y += 2;

            // ------------------ BILL INFO + CUSTOMER BOX ------------------
            const boxYStart = y;

            // Bill Info
            doc.setFontSize(6);
            doc.setFont('helvetica', 'normal');
            y += 3;

            const invNo = header.InvoiceNo || [header.BillNumber, header.FinYear].filter(Boolean).join('/');
            const billDate = header.BillDate ? new Date(header.BillDate).toLocaleDateString('en-GB') : '';
            const billTime = header.BillTime;
            // Use system real-time instead of BillDate time
            // const billTime = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });


            doc.text(`Bill No: ${invNo}`, margin.left + 1, y);
            doc.text(`Date: ${billDate}`, pageWidth - margin.right - 1, y, { align: 'right' });
            y += 3;
            doc.text(`Time: ${billTime}`, pageWidth - margin.right - 1, y, { align: 'right' });
            y += 4;

            // Customer Details
            doc.setFont('helvetica', 'bold');
            doc.text("Customer Details:", margin.left + 1, y);
            y += 3;

            doc.setFont('helvetica', 'normal');
            doc.text(`Name: ${header.CustomerName || ''}`, margin.left + 1, y);
            y += 3;

            if (header.CustomerAddress) {
                doc.text(`Address: ${header.CustomerAddress}`, margin.left + 1, y);
                y += 3;
            }

            if (header.CustomerContactNumber) {
                doc.text(`Contact: ${header.CustomerContactNumber}`, margin.left + 1, y);
                y += 3;
            }

            const boxYEnd = y + 1;

            // Thin box around Bill Info + Customer Details (touching both sides)
            doc.setLineWidth(0.1);
            doc.rect(0.8, boxYStart - 1, pageWidth - 1.6, boxYEnd - boxYStart + 1);

            y = boxYEnd;

            // ------------------ ITEMS TABLE ------------------

            const filteredItems = items.filter(i => (i.Quantity ?? 0) > 0);
            const rows = filteredItems.map((i, idx) => [
                idx + 1,
                i.ItemName || "",
                (i.Quantity ?? 0).toString(),
                (i.Rate ?? 0).toFixed(2),
                (i.DiscountAmount ?? 0).toFixed(2),
                (i.TotalAmount ?? 0).toFixed(2),
            ]);

            autoTable(doc, {
                startY: y,
                margin: { left: margin.left, right: margin.right },
                head: [["Sr", "Product", "Qty", "Rate", "Disc", "Amount"]],
                body: rows,
                theme: "grid",
                styles: {
                    fontSize: 6,
                    cellPadding: 1,
                    lineColor: [0, 0, 0],   // Black color for all cell borders
                    lineWidth: 0.1,
                    textColor: [0, 0, 0]
                },
                headStyles: {
                    fillColor: [255, 255, 255],
                    textColor: [0, 0, 0],
                    lineColor: [0, 0, 0],   // Black header borders
                    lineWidth: 0.1          // Thinner border
                },
                columnStyles: {
                    0: { halign: 'left' },   // Sr
                    1: { halign: 'left' },   // Product
                    2: { halign: 'right' },  // Qty
                    3: { halign: 'right' },  // Rate
                    4: { halign: 'right' },  // Disc
                    5: { halign: 'right' },  // Amount
                },
            });


            let finalY = doc.lastAutoTable.finalY + 3;

            // ------------------ AFTER ITEMS TABLE ------------------
            let tableEndY = doc.lastAutoTable.finalY;

            // Add some space after table
            y = tableEndY + 6;

            // ------------------ TOTALS SECTION ------------------
            doc.setFontSize(6.5);
            doc.setFont('helvetica', 'normal');

            // Total (Right side)
            doc.text(`Total : ${(Number(header.TotalAmount) + Number(header.DiscountAmount)).toFixed(2)}`,
                pageWidth - margin.right, y, { align: 'right' });

            // "YOU SAVED" (Left side, same line)
            if (Number(header.DiscountAmount) > 0) {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(5.5);
                doc.text(`YOU SAVED  ${(Number(header.DiscountAmount) || 0).toFixed(2)}`, margin.left + 1, y);
            }
            y += 4;

            // Bill Discount
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6.5);
            doc.text(`Bill Discount : ${(Number(header.DiscountAmount) || 0).toFixed(2)}`,
                pageWidth - margin.right, y, { align: 'right' });
            y += 4;

            // ---- Add Freight & Packing ----
            if ((Number(header.FreightAmount) + Number(header.FreightGSTAmount)) > 0) {
                doc.text(`Freight : ${((Number(header.FreightAmount) || 0) + (Number(header.FreightGSTAmount) || 0)).toFixed(2)}`,
                    pageWidth - margin.right, y, { align: 'right' });
                y += 4;
            }

            if ((Number(header.PackingAmount) + Number(header.PackingGSTAmount)) > 0) {
                doc.text(`Packing : ${((Number(header.PackingAmount) || 0) + (Number(header.PackingGSTAmount) || 0)).toFixed(2)}`,
                    pageWidth - margin.right, y, { align: 'right' });
                y += 4;
            }

            // Round Off
            doc.text(`Round Off : ${(Number(header.RoundOff) || 0).toFixed(2)}`,
                pageWidth - margin.right, y, { align: 'right' });
            y += 4;

            // Net Total
            doc.setFont('helvetica', 'bold');
            doc.text(`Net Total : ${(Number(header.GrandTotal) || 0).toFixed(2)}`,
                pageWidth - margin.right, y, { align: 'right' });
            y += 1; // extra space below Net Total


            // ------------------ AMOUNT IN WORDS ------------------
            doc.setLineWidth(0.1);
            doc.line(margin.left, y, pageWidth - margin.right, y);
            y += 3;

            // ---- Find the Total Amount from your header or fallback ----
            const grandTotal = Number(header.TotalAmount) || 0;

            // ---- Convert Number to Words ----
            function numberToWords(num) {
                const a = [
                    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven',
                    'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen',
                    'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'
                ];
                const b = [
                    '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty',
                    'Sixty', 'Seventy', 'Eighty', 'Ninety'
                ];

                const inWords = (n) => {
                    if (n < 20) return a[n];
                    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
                    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + inWords(n % 100) : '');
                    if (n < 100000) return inWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + inWords(n % 1000) : '');
                    if (n < 10000000) return inWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + inWords(n % 100000) : '');
                    return inWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + inWords(n % 10000000) : '');
                };

                const parts = num.toFixed(2).split('.');
                let words = inWords(Number(parts[0])) + ' Rupees';
                if (Number(parts[1]) > 0) words += ' and ' + inWords(Number(parts[1])) + ' Paise';
                return words + ' Only';
            }

            // ---- Generate and Print Amount in Words ----
            const amountInWords = numberToWords(grandTotal);

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(4.5);
            doc.text(`Amount in words: ${amountInWords}`, margin.left + 1, y);
            y += 1;

            // ------------------ TERMS & CONDITIONS ------------------

            doc.setLineWidth(0.1);
            doc.line(margin.left, y, pageWidth - margin.right, y);
            y += 3;
            doc.setFont('helvetica', 'bold');
            doc.text("Terms & Conditions", margin.left + 1, y);
            y += 2;

            // ------------------Barcode ------------------

            // doc.setLineWidth(0.1);
            // doc.line(margin.left, y, pageWidth - margin.right, y);
            // y += 3;
            // doc.setFont('helvetica', 'bold');
            // // doc.text("Terms & Conditions", margin.left + 1, y);
            // y += 4;


            // ------------------ QR Code (Left) + Tax Breakdown (Right) ------------------
            const sectionTopY = y; // remember current Y position

            // ---- QR / Barcode Image (LEFT SIDE) ----
            const qrBoxWidth = (pageWidth / 2) - 1.2;
            const qrBoxHeight = 22;

            doc.setLineWidth(0.1);
            // doc.rect(0.8, sectionTopY, qrBoxWidth, qrBoxHeight); // QR container border

            // ✅ Add QR image
            const qrImagePath = Logo; // <-- Update with your image path or base64 data
            try {
                doc.addImage(qrImagePath, "PNG",
                    0.8 + (qrBoxWidth - 18) / 2, // X position centered
                    sectionTopY + (qrBoxHeight - 18) / 2, // Y position centered
                    18, // image width
                    18  // image height
                );
            } catch (error) {
                console.warn("QR image not found:", error);
                doc.setFontSize(6);
                doc.text("QR IMAGE MISSING", 0.8 + qrBoxWidth / 2, sectionTopY + qrBoxHeight / 2, { align: 'center' });
            }

            // ---- Tax Breakdown (RIGHT SIDE SMALL TABLE) ----
            const gstRows = filteredItems.map(i => [
                (Number(i.TaxableAmount) || 0).toFixed(2),
                (Number(i.CGSTAmount) || 0).toFixed(2),
                (Number(i.SGSTAmount) || 0).toFixed(2),
                (Number(i.IGSTAmount) || 0).toFixed(2),
                (Number(i.TotalAmount) || 0).toFixed(2),
            ]);

            const gstTotals = [
                (filteredItems.reduce((s, i) => s + (Number(i.TaxableAmount) || 0), 0)).toFixed(2),
                (filteredItems.reduce((s, i) => s + (Number(i.CGSTAmount) || 0), 0)).toFixed(2),
                (filteredItems.reduce((s, i) => s + (Number(i.SGSTAmount) || 0), 0)).toFixed(2),
                (filteredItems.reduce((s, i) => s + (Number(i.IGSTAmount) || 0), 0)).toFixed(2),
                (filteredItems.reduce((s, i) => s + (Number(i.TotalAmount) || 0), 0)).toFixed(2),
            ];
            gstRows.push(gstTotals);

            // ---- Draw the tax table on the RIGHT ----
            autoTable(doc, {
                startY: sectionTopY + 1,
                margin: { left: qrBoxWidth + 1.2, right: 0.8 },
                tableWidth: pageWidth - qrBoxWidth - 2,
                head: [["Taxable", "CGST", "SGST", "IGST", "Total"]],
                body: gstRows,
                theme: "grid",
                // styles: { fontSize: 5, cellPadding: 1, textColor: [0, 0, 0] },
                // headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], lineWidth: 0.1 },
                styles: {
                    fontSize: 6,
                    cellPadding: 1,
                    lineColor: [0, 0, 0],   // Black color for all cell borders
                    lineWidth: 0.1,
                    textColor: [0, 0, 0]
                },
                headStyles: {
                    fillColor: [255, 255, 255],
                    textColor: [0, 0, 0],
                    lineColor: [0, 0, 0],   // Black header borders
                    lineWidth: 0.1          // Thinner border
                },
                columnStyles: {
                    0: { halign: 'right' },   // Sr
                    1: { halign: 'right' },   // Product
                    2: { halign: 'right' },  // Qty
                    3: { halign: 'right' },  // Rate
                    4: { halign: 'right' },  // Disc                    
                },

                didParseCell: (data) => {
                    if (data.row.index === gstRows.length - 1)
                        data.cell.styles.fontStyle = "bold";
                },
            });

            // ---- Adjust height based on whichever is taller (QR or Table) ----
            const tableEndYs = doc.lastAutoTable.finalY;
            const sectionBottomY = Math.max(sectionTopY + qrBoxHeight, tableEndYs);

            // ---- Outer Rectangle around BOTH ----
            doc.setLineWidth(0.2);
            doc.rect(0.5, sectionTopY - 0.5, pageWidth - 1, sectionBottomY - sectionTopY + 1);

            // ---- Update Y position for next section ----
            y = sectionBottomY + 6;

            // Footer
            doc.setFontSize(6.5);
            doc.setFont('helvetica', 'bold');
            doc.text("THANK YOU - VISIT AGAIN", pageWidth / 2, y, { align: 'center' });

            // ------------------ OUTER BOX END ------------------
            const yEndOuter = y + 2; // small bottom padding
            doc.setLineWidth(0.1);
            doc.rect(0.5, yStartOuter, pageWidth - 1, yEndOuter - yStartOuter);

            // ------------------ PRINT ------------------
            const blob = doc.output('blob');
            const blobUrl = URL.createObjectURL(blob);
            const win = window.open(blobUrl, '_blank');
            if (win) win.onload = () => {
                win.focus();
                win.print();
            };

        } catch (err) {
            console.error("Error generating invoice:", err);
        }
    };

    return (
        <div className="w-full mx-auto" style={{ width: "100%", marginTop: "-25px" }}>
            {/* Filter Bar */}
            <div
                className="flex items-center font-serif gap-3 p-2 rounded-xl shadow-sm"
                style={{ background: "#f9fafb", marginBottom: "10px" }}
            >
                <h6
                    className="uppercase tracking-wide font-serif text-black mt-2 text-gray-700 flex-grow"
                    style={{ fontSize: "14px", fontWeight: "500" }}
                >
                    Sale
                </h6>

                {/* Search */}
                <input
                    type="text"
                    placeholder="🔍 Search..."
                    value={searchQuery}
                    onChange={handleSearch}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    style={{
                        width: isFocused ? "240px" : "180px",
                        height: "30px",
                        border: "1px solid #ddd",
                        borderRadius: "20px",
                        padding: "0 14px",
                        fontSize: "12px",
                        transition: "all 0.3s ease",
                        outline: "none",
                    }}
                />

                {/* From Date */}
                <input
                    type="date"
                    value={fromDate}
                    onChange={handleFromDateChange}
                    style={{
                        height: "30px",
                        fontSize: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "6px",
                        padding: "0 6px",
                        fontFamily: 'Arial, sans-serif',
                    }}
                />

                {/* To Date */}
                <input
                    type="date"
                    value={toDate}
                    onChange={handleToDateChange}
                    style={{
                        height: "30px",
                        fontSize: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "6px",
                        padding: "0 6px",
                        fontFamily: 'Arial, sans-serif',
                    }}
                />

                {/* Refresh Button */}
                <button
                    onClick={fetchtableData}
                    style={{
                        background: "#1090a3",
                        color: "white",
                        fontSize: "12px",
                        border: "none",
                        borderRadius: "20px",
                        padding: "4px 14px",
                        cursor: "pointer",
                        transition: "0.2s",
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.background = "#0b6d7a")}
                    onMouseOut={(e) => (e.currentTarget.style.background = "#1090a3")}
                >
                    Refresh
                </button>

                {hasCreateMasterPermission && (
                    <Tooltip title="Create New Sale" arrow>
                        <Button
                            component={Link}
                            to="/SaleCreate"
                            style={{
                                background: "#3F4F44",
                                color: "white",
                                fontSize: "12px",
                                border: "none",
                                borderRadius: "20px",
                                padding: "4px 14px",
                                cursor: "pointer",
                                transition: "0.2s",
                                fontFamily: 'inherit'
                            }}
                        >
                            Create
                        </Button>
                    </Tooltip>
                )}
            </div>

            {/* Table */}
            <div className='w-full mb-2 flex' style={{ maxHeight: '520px', overflowY: 'auto', marginTop: '-10px' }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        {/* ERP-style Header */}
                        {/* <TableHead>
                            <TableRow style={{
                                backgroundColor: "#0f172a",
                                boxShadow: "inset 0 -3px 6px rgba(0,0,0,0.3)"
                            }}>
                                {["Sl.No", "BILL DATE", "BILL NO", "NAME", "AMOUNT", "CGST", "SGST", "IGST", "TOTAL", "QTY", ""].map(head => (
                                    <TableCell key={head} style={{
                                        fontSize: "11px",
                                        fontWeight: "700",
                                        textTransform: "uppercase",
                                        borderBottom: "2px solid #1e293b",
                                        color: "#ffffff",
                                        //padding: "4px 8px",
                                        padding: "1px",
                                        textAlign: ["AMOUNT", "CGST", "SGST", "IGST", "TOTAL", "QTY"].includes(head) ? "right" : "left",
                                        textShadow: "1px 1px 2px rgba(0,0,0,0.3)"
                                    }}>{head}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead> */}
                        <TableHead>
                            <TableRow
                                style={{
                                    backgroundColor: "#0f172a",
                                    boxShadow: "inset 0 -3px 6px rgba(0,0,0,0.3)"
                                }}
                            >
                                {["Sl.No", "BILL DATE", "BILL NO", "NAME", "AMOUNT", "CGST", "SGST", "IGST", "TOTAL", "QTY", ""].map(
                                    (head) => (
                                        <TableCell
                                            key={head}
                                            style={{
                                                position: "sticky",
                                                top: 0,
                                                zIndex: 2, // ensures header stays above content
                                                backgroundColor: "#0f172a", // reapply background since sticky loses it
                                                fontSize: "11px",
                                                fontWeight: "700",
                                                textTransform: "uppercase",
                                                borderBottom: "2px solid #1e293b",
                                                color: "#ffffff",
                                                padding: "4px 8px",
                                                textAlign: ["AMOUNT", "CGST", "SGST", "IGST", "TOTAL", "QTY"].includes(head)
                                                    ? "right"
                                                    : "left",
                                                textShadow: "1px 1px 2px rgba(0,0,0,0.3)"
                                            }}
                                        >
                                            {head}
                                        </TableCell>
                                    )
                                )}
                            </TableRow>
                        </TableHead>

                        {/* Table Body */}
                        <TableBody sx={{ backgroundColor: 'white' }}>
                            {filteredData.map((item, index) => (
                                <TableRow key={item.id} className={item.IsActive ? '' : 'inactiveRow'} sx={{
                                    '&:hover': { backgroundColor: '#E3FEF7' },
                                    fontSize: '11.5px',
                                    padding: '3px',
                                    cursor: 'pointer'
                                }}>
                                    <TableCell style={{ fontSize: '11.5px', padding: '3px' }}>{index + 1}</TableCell>
                                    <TableCell style={{ fontSize: '11.5px', padding: '3px' }}>{formatDate(item.BillDate)}</TableCell>
                                    <TableCell style={{ fontSize: '11.5px', padding: '3px' }}>{item.BillNumber}/{item.FinYear}</TableCell>
                                    <TableCell style={{ fontSize: '11.5px', padding: '3px' }}>{item.CustomerName}</TableCell>
                                    <TableCell style={{ fontSize: '11.5px', padding: '3px', textAlign: 'right' }}>{item.TaxableAmount}</TableCell>
                                    <TableCell style={{ fontSize: '11.5px', padding: '3px', textAlign: 'right' }}>{item.CGSTAmount}</TableCell>
                                    <TableCell style={{ fontSize: '11.5px', padding: '3px', textAlign: 'right' }}>{item.SGSTAmount}</TableCell>
                                    <TableCell style={{ fontSize: '11.5px', padding: '3px', textAlign: 'right' }}>{item.IGSTAmount}</TableCell>
                                    <TableCell style={{ fontSize: '11.5px', padding: '3px', textAlign: 'right' }}>{item.GrandTotal}</TableCell>
                                    <TableCell style={{ fontSize: '11.5px', padding: '3px', textAlign: 'right' }}>{item.TotalQuantity}</TableCell>
                                    <TableCell style={{ fontSize: '11.5px', padding: '3px', textAlign: 'right' }}>
                                        {/* Actions */}
                                        {hasCreateMasterPermission && (
                                            <Tooltip title="Sale Return" arrow>
                                                <IconButton component={Link} to={`/SaleReturnCreate/${item.Id}`} color="primary">
                                                    <AssignmentReturnIcon style={{ fontSize: '17px' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Sale Details" arrow>
                                            <IconButton component={Link} to={`/SaleDetails/${item.Id}`} color="warning">
                                                <Info style={{ fontSize: '17px' }} />
                                            </IconButton>
                                        </Tooltip>
                                       
                                        {hasCreateMasterPermission && item.IsActive && (
                                            <Tooltip title="Sale A4 Print" arrow>
                                                <IconButton onClick={() => handleA4Print(item.Id)}>
                                                    <PictureAsPdfIcon style={{ fontSize: '15px', color: 'darkmagenta' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {hasEditMasterPermission && item.IsActive && (
                                            <Tooltip title="Sale Edit" arrow>
                                                <IconButton component={Link} to={`/SaleEdit/${item.Id}`} color="primary">
                                                    <EditIcon style={{ fontSize: '17px' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {hasDeleteMasterPermission && item.IsActive && (
                                            <Tooltip title="Sale Delete" arrow>
                                                <IconButton onClick={() => openConfirmDialog(item.Id)} color="error">
                                                    <DeleteIcon style={{ fontSize: '17px' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {!item.IsActive && (
                                            <Tooltip title="Sale Activate" arrow>
                                                <IconButton onClick={() => handleActivate(item.Id)} color="success">
                                                    <ActivateIcon style={{ fontSize: '17px' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>


            </div>

            {/* Confirmation Dialog with ERP-style Header */}
            <Dialog
                open={confirmDialogOpen}
                onClose={closeConfirmDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{ style: { borderRadius: '8px', overflow: 'hidden', minWidth: '350px' } }}
            >
                <DialogTitle
                    id="alert-dialog-title"
                    style={{
                        background: "linear-gradient(120deg, #0f172a, #1e40af)",
                        color: "#fff",
                        fontWeight: "700",
                        fontSize: "14px",
                        padding: "12px 16px",
                        textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
                        borderBottom: "2px solid #0e3b91",
                    }}
                >
                    Delete Record?
                </DialogTitle>
                <DialogContent style={{ padding: "16px", fontSize: "13px", color: "#374151" }}>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this record?
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ padding: "12px 16px", gap: "8px" }}>
                    <button
                        onClick={closeConfirmDialog}
                        style={{
                            backgroundColor: '#dc3545',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 14px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            transition: '0.2s',
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#b02a37')}
                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#dc3545')}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleDelete}
                        style={{
                            backgroundColor: '#28a745',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 14px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            transition: '0.2s',
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#218838')}
                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#28a745')}
                    >
                        Confirm
                    </button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default SaleIndex;



